import { ScatterplotLayer } from '@deck.gl/layers';
import type { GeoFix } from '../map/geolocation.js';

const BLUE: [number, number, number] = [42, 130, 255];
const CORE_RADIUS_PX = 7;
const PULSE_AMP_PX = 18;
const PULSE_PERIOD_SEC = 2.4;
const PULSE_MAX_ALPHA = 140;

/** Datum carried by every location layer; accuracy rides along for the tooltip. */
interface LocationDatum {
  position: [number, number];
  accuracyMeters: number;
}

/** Normalised 0..1 position within the current pulse period. */
export function pulsePhase(nowSec: number, periodSec: number = PULSE_PERIOD_SEC): number {
  return (nowSec % periodSec) / periodSec;
}

/**
 * Three layers for the "you are here" indicator, drawn back-to-front:
 * translucent accuracy halo (meters), an expanding/fading pulse ring (pixels),
 * and a steady blue core dot with a white ring (pixels).
 */
export function buildUserLocationLayers(
  fix: GeoFix | null,
  nowSec: number,
): ScatterplotLayer<LocationDatum>[] {
  if (!fix) return [];

  const t = pulsePhase(nowSec);
  const ringRadius = CORE_RADIUS_PX + PULSE_AMP_PX * t;
  const ringAlpha = Math.round(PULSE_MAX_ALPHA * (1 - t));
  const data: LocationDatum[] = [
    { position: fix.coords, accuracyMeters: fix.accuracyMeters },
  ];

  const accuracy = new ScatterplotLayer<LocationDatum>({
    id: 'location-accuracy',
    data,
    getPosition: (d) => d.position,
    getRadius: (d) => d.accuracyMeters,
    radiusUnits: 'meters',
    radiusMinPixels: 4,
    radiusMaxPixels: 200,
    filled: true,
    getFillColor: [...BLUE, 28] as [number, number, number, number],
    stroked: true,
    getLineColor: [...BLUE, 70] as [number, number, number, number],
    lineWidthMinPixels: 1,
    pickable: false,
  });

  const pulse = new ScatterplotLayer<LocationDatum>({
    id: 'location-pulse',
    data,
    getPosition: (d) => d.position,
    getRadius: ringRadius,
    radiusUnits: 'pixels',
    filled: true,
    getFillColor: [...BLUE, ringAlpha] as [number, number, number, number],
    pickable: false,
  });

  const core = new ScatterplotLayer<LocationDatum>({
    id: 'location',
    data,
    getPosition: (d) => d.position,
    getRadius: CORE_RADIUS_PX,
    radiusUnits: 'pixels',
    filled: true,
    getFillColor: [...BLUE, 255] as [number, number, number, number],
    stroked: true,
    getLineColor: [255, 255, 255, 255] as [number, number, number, number],
    lineWidthMinPixels: 2,
    pickable: true,
  });

  return [accuracy, pulse, core];
}
